/**
 * =============================================================================
 * DealCadenceTab — Aba "Cadência" do DealDetailsModalPremium
 *
 * Versão: 2.3.2
 *
 * Changelog:
 *  - 2.3.2: Corrige vazamento de steps entre fases. Steps sem `phase_id`
 *           (playbooks legados que não vincularam steps a fases) deixavam de
 *           ser filtrados e apareciam em TODAS as fases do stepper. Agora
 *           esses steps órfãos são exibidos apenas na fase-âncora (primeira
 *           fase do pipeline, por sort_order), restaurando o comportamento
 *           anterior em que cada fase mostra somente as suas atividades.
 *  - 2.3.0: Oculta o botão "Criar" (atividade manual) quando a cadência do
 *           negócio já está encerrada (nenhum step com cadence_status='active')
 *           e exibe um InlineNotice informando que a cadência foi concluída.
 *           Evita erro server-side "cadência não ativa" após preencher o form.
 *  - 2.0.0: Reescrita completa. Adota o mesmo layout premium da aba "Geral"
 *           do ActivityDetailsModalPremium, reaproveitando ActivityPhaseStepper
 *           (setas de fase), ActivityPhaseList (lista lateral de steps por fase)
 *           e ExecuteStepBody (painel de roteiro do step). Sem aba Histórico —
 *           a timeline de atividades já existe na aba Resumo do modal.
 *  - 1.0.0: Versão inicial em formato de checklist vertical (descontinuada).
 * =============================================================================
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, ListChecks, XCircle, Plus, Phone, MessageCircle, Mail, Calendar as CalendarIcon, MapPin, StickyNote, CheckSquare, Zap, Hand } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invalidateActivityListCaches } from '@/lib/cache/invalidateActivityListCaches';

import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCrmDeal } from '@/hooks/useCrmDeals';
import { useDealCadence } from '@/hooks/useDealCadence';
import { usePipelinePhaseStats } from '@/hooks/usePipelinePhaseStats';
import { usePermissions } from '@/hooks/usePermissions';
import { useCrmPhasePermissions } from '@/hooks/useCrmPhasePermissions';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { InlineNotice } from '@/components/ui/inline-notice';

// Tipos de atividade — mesmo dock/ícones/cores do CrmPlaybookEditor
const MANUAL_ACTIVITY_TYPES: { value: string; label: string; icon: any }[] = [
  { value: 'call',     label: 'Ligação',  icon: Phone },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { value: 'email',    label: 'E-mail',   icon: Mail },
  { value: 'meeting',  label: 'Reunião',  icon: CalendarIcon },
  { value: 'visit',    label: 'Visita',   icon: MapPin },
  { value: 'note',     label: 'Anotação', icon: StickyNote },
  { value: 'task',     label: 'Lembrete', icon: CheckSquare },
];
const MANUAL_ACTIVITY_TYPE_HOVER: Record<string, string> = {
  call:     'hover:text-emerald-500 hover:border-emerald-400/60',
  whatsapp: 'hover:text-green-500 hover:border-green-400/60',
  email:    'hover:text-sky-500 hover:border-sky-400/60',
  meeting:  'hover:text-indigo-500 hover:border-indigo-400/60',
  visit:    'hover:text-orange-500 hover:border-orange-400/60',
  note:     'hover:text-amber-500 hover:border-amber-400/60',
  task:     'hover:text-violet-500 hover:border-violet-400/60',
};
const MANUAL_ACTIVITY_TYPE_ACTIVE: Record<string, string> = {
  call:     'text-emerald-500 border-emerald-400/60 bg-emerald-500/10',
  whatsapp: 'text-green-500 border-green-400/60 bg-green-500/10',
  email:    'text-sky-500 border-sky-400/60 bg-sky-500/10',
  meeting:  'text-indigo-500 border-indigo-400/60 bg-indigo-500/10',
  visit:    'text-orange-500 border-orange-400/60 bg-orange-500/10',
  note:     'text-amber-500 border-amber-400/60 bg-amber-500/10',
  task:     'text-violet-500 border-violet-400/60 bg-violet-500/10',
};
import { cn } from '@/lib/utils';
import { ptBR } from 'date-fns/locale';
import { ActivityPhaseStepper } from '@/components/crm/activities/modal/ActivityPhaseStepper';
import { ActivityPhaseList } from '@/components/crm/activities/modal/ActivityPhaseList';
import {
  ExecuteStepBody,
  type ExecuteStepDealContext,
} from '@/components/crm/deals/cadence/ExecuteStepBody';

interface DealCadenceTabProps {
  dealId: string;
  isActive: boolean;
  /** Contexto do deal para popular variáveis do roteiro e atalhos (WhatsApp, tel, mailto) */
  dealContext?: ExecuteStepDealContext;
  /** Step a pré-selecionar (ex.: ao abrir vindo da listagem de Atividades) */
  initialStepId?: string | null;
}

/**
 * Aba Cadência: stepper de fases (setas) + lista lateral + roteiro do step.
 * Mesmo layout da aba Geral do modal de Atividades, embutido no modal de Negócio.
 */
export function DealCadenceTab({ dealId, isActive, dealContext, initialStepId }: DealCadenceTabProps) {
  const isMobile = useIsMobile();
  const { profile, currentTenant } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ----- Permissões globais ---------------------------------------------------
  const { canView, canEdit, canDelete } = usePermissions();
  const canViewDeals = canView('crm_deals');
  const canEditDeals = canEdit('crm_deals');
  const canCancel = canDelete('crm_cadence');

  // ----- Dados do negócio + fases + cadência ---------------------------------
  const { data: deal, isLoading: isDealLoading } = useCrmDeal(isActive ? dealId : null);
  const workspaceId = (deal as any)?.pipeline?.workspace_id ?? null;
  const phasePerms = useCrmPhasePermissions(workspaceId);
  const { phases } = usePipelinePhaseStats(deal?.pipeline_id ?? null);
  const {
    steps,
    isLoading: isCadenceLoading,
    completeStep,
    skipStep,
    cancelCadence,
    reopenStep,
    cancelStep,
    createManualStep,
    updateManualStep,
    editCompletionDate,
  } = useDealCadence(isActive ? dealId : null);

  // [v2.1.0] Busca flags de permissão do playbook (allow_seller_cancel_step /
  // allow_seller_create_manual_step). Admin bypass via canDelete('crm_cadence').
  const playbookId = steps[0]?.playbook_id ?? null;
  const { data: playbookFlags } = useQuery({
    queryKey: ['playbook-flags', playbookId],
    enabled: !!playbookId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('crm_activity_playbooks')
        .select('allow_seller_cancel_step, allow_seller_create_manual_step')
        .eq('id', playbookId as string)
        .maybeSingle();
      if (error) throw error;
      return data as {
        allow_seller_cancel_step: boolean | null;
        allow_seller_create_manual_step: boolean | null;
      } | null;
    },
  });
  const allowCancelStep = !!(canCancel || playbookFlags?.allow_seller_cancel_step);
  const allowManualCreate = !!(canCancel || playbookFlags?.allow_seller_create_manual_step);

  // ----- Estado: fase + step selecionados ------------------------------------
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  /**
   * Estado dos critérios de sucesso marcados, indexado por stepId. Precisa
   * viver no container (e não em ExecuteStepBody) porque o botão "Concluir"
   * está no ActivityPhaseList e precisa enviar o outcome à RPC
   * `complete_cadence_step`.
   */
  const [criteriaByStep, setCriteriaByStep] = useState<Record<string, Record<string, boolean>>>({});

  /**
   * Hidrata `criteriaByStep` a partir do `outcome` persistido em cada step
   * (RPC `list_deal_cadence_steps` v1.2.0). Estratégia:
   *  - Steps `completed`/`skipped` são somente-leitura → sempre sobrescreve
   *    com o outcome salvo (fonte da verdade).
   *  - Steps `pending`/`in_progress` → só popula se o estado local ainda não
   *    contém aquele step, para preservar edições em andamento do usuário.
   */
  useEffect(() => {
    if (!steps || steps.length === 0) return;
    setCriteriaByStep((prev) => {
      const next: Record<string, Record<string, boolean>> = { ...prev };
      let changed = false;
      for (const step of steps) {
        const list = step.outcome?.success_criteria;
        if (!Array.isArray(list) || list.length === 0) continue;
        const isReadonly = step.step_status === 'completed' || step.step_status === 'skipped';
        if (!isReadonly && next[step.step_id]) continue; // preserva edição em curso
        const map: Record<string, boolean> = {};
        for (const c of list) {
          if (c && typeof c.id === 'string') map[c.id] = !!c.checked;
        }
        next[step.step_id] = map;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [steps]);


  // Inicializa quando o deal carrega
  useEffect(() => {
    if (!deal) return;
    setSelectedPhaseId((prev) => prev ?? deal.phase_id ?? null);
  }, [deal?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pré-seleciona step vindo de fora (ex.: clique na listagem de Atividades).
  // Aplica apenas uma vez por (dealId + initialStepId) para não brigar com
  // a interação manual posterior do usuário.
  const appliedInitialStepRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialStepId || !steps.length) return;
    const key = `${dealId}:${initialStepId}`;
    if (appliedInitialStepRef.current === key) return;
    const target = steps.find((s) => s.step_id === initialStepId);
    if (!target) return;
    appliedInitialStepRef.current = key;
    setSelectedPhaseId(target.phase_id ?? deal?.phase_id ?? null);
    setSelectedStepId(target.step_id);
  }, [initialStepId, steps, dealId, deal?.phase_id]);

  // Filtra steps da fase selecionada.
  // Importante: NÃO fazer fallback para `steps` quando a fase não tiver itens —
  // caso contrário a coluna central acabaria mostrando o roteiro de um step
  // de outra fase (ex.: "Novo lead" enquanto o usuário está em "Enviar proposta").
  // Fase-âncora para steps órfãos (phase_id = NULL). Playbooks legados podem
  // ter steps sem vínculo de fase; para evitar duplicação em todas as fases,
  // atribuímos esses steps à 1ª fase do pipeline (por sort_order). Aparece
  // uma única vez, na fase inicial — comportamento intuitivo.
  const anchorPhaseId = useMemo(
    () => (phases.length ? phases[0].phase_id : null),
    [phases],
  );

  const stepsInPhase = useMemo(() => {
    if (!selectedPhaseId) return steps;
    return steps.filter((s) => {
      if (s.phase_id === selectedPhaseId) return true;
      // Órfão (sem phase_id) aparece somente na fase-âncora
      if (s.phase_id == null && selectedPhaseId === anchorPhaseId) return true;
      return false;
    });
  }, [steps, selectedPhaseId, anchorPhaseId]);

  // [v2.3.0] Cadência ativa? Derivado de `cadence_status` já retornado por
  // `list_deal_cadence_steps`. Se nenhum step estiver com cadência ativa,
  // consideramos a cadência encerrada (completed/cancelled) e bloqueamos a
  // criação de novos steps manuais na UI.
  const isCadenceActive = useMemo(
    () => steps.some((s: any) => s?.cadence_status === 'active'),
    [steps],
  );

  // Auto-seleciona o 1º step pendente da fase ao trocar
  useEffect(() => {
    if (!stepsInPhase.length) {
      setSelectedStepId(null);
      return;
    }
    const stillThere = stepsInPhase.some((s) => s.step_id === selectedStepId);
    if (!stillThere) {
      const firstPending = stepsInPhase.find(
        (s) => s.step_status === 'pending' || s.step_status === 'in_progress',
      );
      setSelectedStepId(firstPending?.step_id ?? stepsInPhase[0].step_id);
    }
  }, [stepsInPhase, selectedStepId]);

  const activeStep = stepsInPhase.find((s) => s.step_id === selectedStepId) ?? null;

  // ----- Contexto mesclado para variáveis e atalhos do roteiro ---------------
  const mergedContext: ExecuteStepDealContext = useMemo(
    () => ({
      ...(dealContext ?? {}),
      dealId: dealContext?.dealId ?? dealId,
      contact:
        dealContext?.contact ??
        (deal?.contact
          ? { name: deal.contact.name, phone: deal.contact.phone, email: deal.contact.email }
          : null),
      deal:
        dealContext?.deal ??
        (deal
          ? {
              title: deal.title,
              value: deal.value as number | null,
              currency: deal.currency,
              phase: (deal as any).phase?.name ?? null,
              pipeline: deal.pipeline?.name ?? null,
            }
          : null),
      seller: dealContext?.seller ?? { full_name: profile?.full_name ?? null },
      tenant: dealContext?.tenant ?? { name: currentTenant?.name ?? null },
    }),
    [dealContext, deal, dealId, profile, currentTenant],
  );

  // ----- Permissões resolvidas para a fase selecionada -----------------------
  const canViewSelectedPhase = phasePerms.isGranularEnabled
    ? phasePerms.canDoOnPhase(selectedPhaseId, 'can_view')
    : canViewDeals;
  const canEditSelectedPhase = phasePerms.isGranularEnabled
    ? phasePerms.canDoOnPhase(selectedPhaseId, 'can_edit')
    : canEditDeals;
  const canViewPhase = (phaseId: string) =>
    phasePerms.isGranularEnabled ? phasePerms.canDoOnPhase(phaseId, 'can_view') : canViewDeals;

  // ----- Handlers -------------------------------------------------------------
  const handleComplete = async (stepId: string) => {
    if (!canEditSelectedPhase) {
      toast({
        title: 'Sem permissão',
        description: 'Você não pode concluir atividades nesta fase.',
        variant: 'destructive',
      });
      return;
    }
    // Monta outcome com critérios de sucesso marcados pelo usuário. A RPC
    // `complete_cadence_step` persiste esse jsonb em crm_deal_cadence_steps.outcome.
    const step = steps.find((s) => s.step_id === stepId);
    const criteria = criteriaByStep[stepId] ?? {};
    const criteriaList = Array.isArray(step?.success_criteria) ? step!.success_criteria : [];
    const outcome =
      criteriaList.length > 0
        ? {
            success_criteria: criteriaList.map((c: any) => ({
              id: c.id,
              label: c.label,
              required: !!c.required,
              checked: !!criteria[c.id],
            })),
          }
        : null;
    try {
      await completeStep.mutateAsync({ stepId, outcome });
      toast({ title: 'Atividade concluída' });
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message ?? '', variant: 'destructive' });
    }
  };


  const handleSkip = async (stepId: string) => {
    if (!canEditSelectedPhase) return;
    try {
      await skipStep.mutateAsync({ stepId });
      toast({ title: 'Atividade pulada' });
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message ?? '', variant: 'destructive' });
    }
  };

  const handleToggleComplete = (stepId: string, currentlyDone: boolean) => {
    if (currentlyDone) return; // reabertura vai pelo menu (item "Reabrir atividade")
    void handleComplete(stepId);
  };

  /**
   * Reabre um passo previamente concluído/pulado. Útil quando o usuário
   * clicou por engano no checkbox de conclusão.
   */
  const handleReopen = async (stepId: string) => {
    if (!canEditSelectedPhase) {
      toast({
        title: 'Sem permissão',
        description: 'Você não pode reabrir atividades nesta fase.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await reopenStep.mutateAsync(stepId);
      toast({ title: 'Atividade reaberta' });
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message ?? '', variant: 'destructive' });
    }
  };

  /**
   * Reagenda um passo de cadência via RPC `bulk_activities_action`.
   * Após sucesso a RPC já registra o evento `step_rescheduled` na timeline
   * do negócio (ver migration v1.1.0). Invalidamos os caches relacionados.
   */
  const handleReschedule = async (stepId: string, isoDateTime: string) => {
    if (!canEditSelectedPhase) {
      toast({
        title: 'Sem permissão',
        description: 'Você não pode reagendar atividades nesta fase.',
        variant: 'destructive',
      });
      return;
    }
    try {
      const { data, error } = await supabase.rpc('bulk_activities_action' as never, {
        p_items: [{ origin: 'cadence', id: stepId }] as never,
        p_action: 'reschedule' as never,
        p_due_at: isoDateTime as never,
        p_tenant_hint: currentTenant?.id ?? null as never,
      } as never);
      if (error) throw error;
      const errors = (data as any)?.errors ?? [];
      if (Array.isArray(errors) && errors.length > 0) {
        throw new Error(errors[0]?.error || 'Falha ao reagendar');
      }
      // Invalida cadência (lista de steps + próxima ação) e timeline do negócio
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['deal-cadence-steps', dealId] }),
        queryClient.invalidateQueries({ queryKey: ['deal-cadence-next', dealId] }),
        queryClient.invalidateQueries({ queryKey: ['deal-activities', dealId] }),
        // Atualiza listagem global de atividades, feed do CRM e KPIs
        invalidateActivityListCaches(queryClient),
      ]);
      toast({ title: 'Atividade reagendada' });
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message ?? '', variant: 'destructive' });
    }
  };

  // [v2.1.0] Cancelar step individual (motivo obrigatório vem do dialog do child)
  const handleCancelStep = async (stepId: string, reason: string) => {
    try {
      await cancelStep.mutateAsync({ stepId, reason });
      await invalidateActivityListCaches(queryClient);
      toast({ title: 'Atividade cancelada' });
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message ?? '', variant: 'destructive' });
    }
  };

  // [v2.1.0] Ajusta data histórica de conclusão de step da cadência
  const handleEditCompletionDate = async (stepId: string, iso: string) => {
    try {
      await editCompletionDate.mutateAsync({ kind: 'cadence', id: stepId, completedAt: iso });
      await invalidateActivityListCaches(queryClient);
      toast({ title: 'Data de conclusão atualizada' });
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message ?? '', variant: 'destructive' });
    }
  };

  // [v2.1.0 / v2.2.0] Estado + submit do dialog "Criar/Editar atividade manual"
  const [manualOpen, setManualOpen] = useState(false);
  const [editingStepId, setEditingStepId] = useState<string | null>(null); // v2.2.0
  const [manualType, setManualType] = useState<string>('task');
  const [manualTitle, setManualTitle] = useState<string>('');
  const [manualDate, setManualDate] = useState<Date | undefined>(new Date());
  const [manualTime, setManualTime] = useState<string>('09:00');
  const [manualDesc, setManualDesc] = useState<string>('');
  const resetManual = () => {
    setEditingStepId(null);
    setManualType('task');
    setManualTitle('');
    setManualDate(new Date());
    setManualTime('09:00');
    setManualDesc('');
  };

  // v2.2.0 — abre o modal em modo edição pré-preenchendo a partir do step manual
  const handleEditManualStep = (step: any) => {
    setEditingStepId(step.step_id);
    setManualType(step.activity_type || 'task');
    setManualTitle(step.title || '');
    const due = step.due_at ? new Date(step.due_at) : new Date();
    setManualDate(due);
    const pad = (n: number) => String(n).padStart(2, '0');
    setManualTime(`${pad(due.getHours())}:${pad(due.getMinutes())}`);
    // Descrição HTML — passamos como texto simples (Textarea). Sem parsing.
    setManualDesc(step.description_html || '');
    setManualOpen(true);
  };

  const submitManual = async () => {
    if (!manualTitle.trim() || !manualDate) return;
    const [h, m] = manualTime.split(':').map((v) => parseInt(v, 10) || 0);
    const due = new Date(manualDate);
    due.setHours(h, m, 0, 0);
    try {
      if (editingStepId) {
        // Modo edição
        await updateManualStep.mutateAsync({
          stepId: editingStepId,
          activityType: manualType,
          title: manualTitle.trim(),
          dueAt: due.toISOString(),
          descriptionHtml: manualDesc.trim() ? manualDesc.trim() : null,
          phaseId: selectedPhaseId,
        });
        await invalidateActivityListCaches(queryClient);
        toast({ title: 'Atividade atualizada' });
      } else {
        // Modo criação
        await createManualStep.mutateAsync({
          activityType: manualType,
          title: manualTitle.trim(),
          dueAt: due.toISOString(),
          descriptionHtml: manualDesc.trim() ? manualDesc.trim() : null,
          phaseId: selectedPhaseId,
        });
        await invalidateActivityListCaches(queryClient);
        toast({ title: 'Atividade manual criada' });
      }
      setManualOpen(false);
      resetManual();
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message ?? '', variant: 'destructive' });
    }
  };

  const handleCancelCadence = async () => {
    if (!steps.length) return;
    if (!window.confirm('Cancelar a cadência? Os passos pendentes serão marcados como pulados.'))
      return;
    try {
      await cancelCadence.mutateAsync({ cadenceId: steps[0].cadence_id });
      toast({ title: 'Cadência cancelada' });
    } catch (e: any) {
      toast({ title: 'Erro', description: e?.message ?? '', variant: 'destructive' });
    }
  };

  // ----- Estados de carregamento e vazio -------------------------------------
  const isLoading = isDealLoading || (isCadenceLoading && !steps.length);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Carregando cadência...
      </div>
    );
  }

  if (!steps.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
        <ListChecks className="h-10 w-10 mb-3 opacity-40" />
        <p className="text-sm font-medium">Nenhuma cadência ativa</p>
        <p className="text-xs mt-1 max-w-xs">
          Este negócio ainda não possui playbook em execução. Cadências são iniciadas
          automaticamente conforme os bindings configurados no workspace.
        </p>
      </div>
    );
  }

  // ----- Render ---------------------------------------------------------------
  return (
    <div
      className={
        isMobile
          ? 'flex flex-col -m-4'
          : 'flex flex-col h-full min-h-0 -m-4 md:-m-6'
      }
    >
      {/* Stepper de fases (setas) */}
      {phases.length > 0 && (
        <div className="border-b shrink-0 bg-muted/20">
          <ActivityPhaseStepper
            phases={phases}
            currentPhaseId={deal?.phase_id ?? null}
            selectedPhaseId={selectedPhaseId}
            onSelectPhase={(id) => setSelectedPhaseId(id)}
            canViewPhase={canViewPhase}
            /* v1.x — Avatar do vendedor removido das setas de fase a pedido do produto */
          />
        </div>
      )}

      {/* Conteúdo: dois "blocos-card" (esquerda: próximas atividades;
          direita: roteiro do step), no mesmo padrão dos blocos do sidebar
          direito do modal — bordas suaves, headers uppercase e respiro. */}
      <div
        className={
          isMobile
            ? 'flex flex-col gap-3 p-3 bg-muted/20'
            : 'flex flex-1 min-h-0 flex-row gap-4 p-4 bg-muted/20'
        }
      >
        {/* Card: Próximas atividades */}
        <section
          className={
            isMobile
              ? 'rounded-lg border bg-card flex flex-col overflow-hidden'
              : 'rounded-lg border bg-card w-[360px] shrink-0 flex flex-col min-h-0 overflow-hidden'
          }
        >
          <header className="px-4 py-2.5 border-b shrink-0 flex items-center justify-between gap-2 bg-card">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Próximas atividades
            </h3>
            <div className="flex items-center gap-1">
              {/* [v2.1.0] Criar step manual — permissão do playbook (ou admin)
                  [v2.3.0] Só aparece se a cadência estiver ativa. Se estiver
                  concluída/cancelada, o InlineNotice abaixo informa o usuário. */}
              {allowManualCreate && canEditSelectedPhase && isCadenceActive && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setManualOpen(true)}
                  className="h-6 gap-1 text-[10px] text-primary hover:text-primary hover:bg-primary/10"
                  title="Criar atividade manual nesta cadência"
                >
                  <Plus className="h-3 w-3" />
                  Criar
                </Button>
              )}
              {canCancel && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCancelCadence}
                  disabled={cancelCadence.isPending}
                  className="h-6 gap-1 text-[10px] text-destructive hover:text-destructive hover:bg-destructive/10"
                  title="Cancelar a cadência inteira"
                >
                  <XCircle className="h-3 w-3" />
                  Cancelar
                </Button>
              )}
            </div>
          </header>
          {/* [v2.3.0] Aviso quando a cadência já foi encerrada. */}
          {!isCadenceActive && steps.length > 0 && (
            <div className="px-3 pt-2">
              <InlineNotice variant="info">
                Cadência concluída. Não é possível adicionar novas atividades manuais.
              </InlineNotice>
            </div>
          )}
          <div className={isMobile ? '' : 'flex-1 min-h-0 overflow-y-auto'}>
            {!canViewSelectedPhase ? (
              <div className="p-6 text-center text-xs text-muted-foreground">
                Sem permissão para visualizar atividades desta fase.
              </div>
            ) : (() => {
              // Particiona steps DA FASE SELECIONADA em automáticos (playbook) e manuais (ad-hoc)
              // [v2.3.1] Usa stepsInPhase (não steps) para respeitar troca de fase no stepper.
              const automaticSteps = stepsInPhase.filter((s: any) => !s.is_manual);
              const manualSteps = stepsInPhase.filter((s: any) => s.is_manual);

              const commonProps = {
                phaseId: selectedPhaseId,
                selectedStepId,
                onSelectStep: setSelectedStepId,
                onToggleComplete: handleToggleComplete,
                onSkip: handleSkip,
                onReschedule: handleReschedule,
                onReopen: handleReopen,
                onCancel: handleCancelStep,
                onEditCompletionDate: handleEditCompletionDate,
                onEditManualStep: handleEditManualStep, // v2.2.0
                canEditManualStep: allowManualCreate,   // v2.2.0 — mesma flag de criar
                canCancelStep: allowCancelStep,
                canComplete: canEditSelectedPhase,
                isBusy:
                  completeStep.isPending ||
                  skipStep.isPending ||
                  reopenStep.isPending ||
                  cancelStep.isPending ||
                  editCompletionDate.isPending ||
                  updateManualStep.isPending,
                disableInternalScroll: true,
              };

              return (
                <div className="flex flex-col">
                  {/* Seção: Automática (steps do playbook) */}
                  <div>
                    <div className="px-4 pt-3 pb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <Zap className="h-3 w-3 text-primary" />
                      Automática
                      <span className="ml-1 text-muted-foreground/70 normal-case tracking-normal">
                        ({automaticSteps.length})
                      </span>
                    </div>
                    <ActivityPhaseList steps={automaticSteps} {...commonProps} />
                  </div>

                  {/* Seção: Manual (steps ad-hoc) — só aparece quando houver */}
                  {manualSteps.length > 0 && (
                    <div className="border-t mt-1">
                      <div className="px-4 pt-3 pb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        <Hand className="h-3 w-3 text-amber-600" />
                        Manual
                        <span className="ml-1 text-muted-foreground/70 normal-case tracking-normal">
                          ({manualSteps.length})
                        </span>
                      </div>
                      <ActivityPhaseList steps={manualSteps} {...commonProps} />
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </section>

        {/* Card: Roteiro da atividade */}
        <section
          className={
            isMobile
              ? 'rounded-lg border bg-card flex flex-col overflow-hidden'
              : 'rounded-lg border bg-card flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden'
          }
        >
          <header className="px-4 py-2.5 border-b shrink-0 flex items-center justify-between gap-2 bg-card">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Roteiro da atividade
            </h3>
          </header>
          <div className={isMobile ? '' : 'flex-1 min-h-0 overflow-hidden flex flex-col'}>
            {activeStep ? (
              <ExecuteStepBody
                step={activeStep}
                context={mergedContext}
                isBusy={completeStep.isPending || skipStep.isPending}
                canSkip={canEditSelectedPhase}
                hideFooter
                disableInternalScroll={isMobile}
                checkedCriteria={criteriaByStep[activeStep.step_id] ?? {}}
                onCriteriaChange={(next) =>
                  setCriteriaByStep((prev) => ({ ...prev, [activeStep.step_id]: next }))
                }
              />

            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-muted-foreground">
                <ListChecks className="h-10 w-10 mb-3 opacity-40" />
                <p className="text-sm font-medium">Nenhuma atividade nesta fase</p>
                <p className="text-xs mt-1 max-w-xs">
                  Selecione outra fase no topo para visualizar o roteiro das atividades.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* [v2.1.0] Dialog — Criar atividade manual dentro da cadência ativa */}
      <Dialog open={manualOpen} onOpenChange={(open) => { setManualOpen(open); if (!open) resetManual(); }}>
        <DialogContent className="sm:max-w-[460px] p-4 max-h-[85dvh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="text-sm">
              {editingStepId ? 'Editar atividade manual' : 'Nova atividade manual'}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {editingStepId
                ? 'Ajuste os dados desta atividade ad-hoc. Alterações não impactam métricas de aderência ao playbook.'
                : 'Adicione um passo ad-hoc a esta cadência. Não impacta métricas de aderência ao playbook.'}
            </DialogDescription>
          </DialogHeader>

          {/* Miolo rolável — evita que o modal cresça além da viewport */}
          <div className="flex-1 overflow-y-auto pr-1 -mr-1 space-y-2 mt-1">
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Título <span className="text-destructive">*</span></Label>
                <Input
                  value={manualTitle}
                  onChange={(e) => setManualTitle(e.target.value)}
                  placeholder="Ex.: Ligação de follow-up"
                  className="h-8 text-xs"
                  maxLength={200}
                />
              </div>

              {/* Tipo — dock horizontal com ícones (mesmo padrão visual do CrmPlaybookEditor) */}
              <div>
                <Label className="text-xs">Tipo</Label>
                <TooltipProvider delayDuration={200}>
                  <div className="flex items-center gap-1.5 rounded-lg border bg-muted/30 p-1.5">
                    {MANUAL_ACTIVITY_TYPES.map((a) => {
                      const isActive = manualType === a.value;
                      const hover = MANUAL_ACTIVITY_TYPE_HOVER[a.value] ?? 'hover:bg-accent';
                      const active = MANUAL_ACTIVITY_TYPE_ACTIVE[a.value] ?? '';
                      return (
                        <Tooltip key={a.value}>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => setManualType(a.value)}
                              className={cn(
                                'h-9 w-9 rounded-md inline-flex items-center justify-center border border-transparent text-muted-foreground transition-colors',
                                isActive ? active : hover,
                              )}
                              aria-label={a.label}
                              aria-pressed={isActive}
                            >
                              <a.icon className="h-4 w-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top">{a.label}</TooltipContent>
                        </Tooltip>
                      );
                    })}
                  </div>
                </TooltipProvider>
              </div>

              <div>
                <Label className="text-xs">Hora</Label>
                <Input
                  type="time"
                  value={manualTime}
                  onChange={(e) => setManualTime(e.target.value)}
                  className="h-8 text-xs w-32"
                />
              </div>
            </div>


            <Label className="text-xs mt-2 block">Data <span className="text-destructive">*</span></Label>
            <Calendar
              mode="single"
              locale={ptBR}
              selected={manualDate}
              onSelect={(d) => setManualDate(d ?? undefined)}
              initialFocus
              className={cn('p-0 pointer-events-auto scale-95 origin-top')}
            />

            <Label className="text-xs mt-2 block">Descrição (opcional)</Label>
            <Textarea
              value={manualDesc}
              onChange={(e) => setManualDesc(e.target.value)}
              placeholder="Detalhes ou instruções para esta atividade"
              rows={3}
              className="text-xs"
              maxLength={2000}
            />
          </div>

          <DialogFooter className="mt-3 gap-2 sm:gap-2 shrink-0">
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setManualOpen(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              className="h-8 text-xs"
              disabled={
                !manualTitle.trim() ||
                !manualDate ||
                createManualStep.isPending ||
                updateManualStep.isPending
              }
              onClick={submitManual}
            >
              {editingStepId
                ? (updateManualStep.isPending ? 'Salvando...' : 'Salvar alterações')
                : (createManualStep.isPending ? 'Criando...' : 'Criar atividade')}
            </Button>
          </DialogFooter>

        </DialogContent>
      </Dialog>
    </div>
  );
}
